import { ChangeEvent, useEffect, useState } from 'react'
import * as XLSX from 'xlsx'
import { useAuth } from '../../auth/AuthProvider'
import {
  obterStatusAdminFiqueEmDia, restaurarBaseFiqueEmDia, substituirBaseFiqueEmDia,
  type FiqueAdminStatus,
} from '../../lib/fiqueEmDia'
import {
  checksumFiqueEmDia, parseFiqueCsv, parseFiqueEmDiaRows, type FiqueParseResult,
} from '../../lib/fiqueEmDiaParser'

const EMPTY: FiqueParseResult = { registros: [], rejected: [], headers: [], fatal: [], sourceRows: 0, invalid: [], duplicates: 0, referenceDate: null, documentStats: { cpfValidos: 0, cnpjValidos: 0, cpfInvalidos: 0, cnpjInvalidos: 0, irrecuperaveis: 0 } }

function mostrarData(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '—'
}

export function FiqueEmDiaAdmin({ onBack }: { onBack: () => void }) {
  const { profile } = useAuth()
  const [snapshot, setSnapshot] = useState<FiqueAdminStatus>({ active: null, previous: null })
  const [loading, setLoading] = useState(true)
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<FiqueParseResult>(EMPTY)
  const [checksum, setChecksum] = useState('')
  const [processing, setProcessing] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [confirmExclusions, setConfirmExclusions] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function reload() {
    setLoading(true)
    try {
      setSnapshot(await obterStatusAdminFiqueEmDia())
    } catch {
      setError('Não foi possível carregar a administração da campanha.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (profile?.role === 'administrador') void reload()
    else setLoading(false)
  }, [profile?.role])

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const inputFile = event.target.files?.[0] ?? null
    event.target.value = ''
    setFile(null)
    setParsed(EMPTY)
    setChecksum('')
    setMessage('')
    setError('')
    setConfirmExclusions(false)
    if (!inputFile) return
    if (inputFile.size > 20 * 1024 * 1024) {
      setError('O arquivo excede o limite de 20 MB.')
      return
    }
    setProcessing(true)
    try {
      let rows: unknown[][]
      if (/\.(csv|tsv|txt)$/i.test(inputFile.name)) {
        rows = parseFiqueCsv(await inputFile.text())
      } else if (/\.(xlsx|xls)$/i.test(inputFile.name)) {
        const workbook = XLSX.read(await inputFile.arrayBuffer(), { type: 'array', cellDates: false })
        if (!workbook.SheetNames.length) throw new Error('Nenhuma planilha encontrada.')
        rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {
          header: 1, raw: false, defval: '',
        }) as unknown[][]
      } else {
        throw new Error('Formato não aceito.')
      }
      const result = parseFiqueEmDiaRows(rows)
      if (result.sourceRows > 150000) {
        const reason = 'Esta versão aceita até 150.000 registros por importação.'
        result.fatal.push(reason)
        result.invalid.push(reason)
      }
      setParsed(result)
      setFile(inputFile)
      if (!result.fatal.length && result.registros.length > 0 &&
          result.sourceRows === result.registros.length + result.rejected.length) {
        setChecksum(await checksumFiqueEmDia(result.registros, result.referenceDate!))
      }
    } catch {
      setError('Não foi possível interpretar o arquivo. Verifique o formato e os cabeçalhos.')
    } finally {
      setProcessing(false)
    }
  }

  function downloadExcluded() {
    if (!file || !parsed.rejected.length || profile?.role !== 'administrador') return
    // XLSX, em vez de CSV, preserva zeros iniciais de CPF/CNPJ e mantém texto como texto,
    // sem converter conteúdos suspeitos em fórmulas do Excel.
    const report = [
      ['LINHA_ORIGINAL', 'MOTIVO_EXCLUSAO', ...parsed.headers],
      ...parsed.rejected.map((item) => [item.line, item.reason, ...item.values]),
    ]
    const worksheet = XLSX.utils.aoa_to_sheet(report)
    worksheet['!cols'] = [{ wch: 17 }, { wch: 85 }, ...parsed.headers.map(() => ({ wch: 24 }))]
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, worksheet, 'Registros excluídos')
    const safeName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 65)
    XLSX.writeFile(book, 'Fique_em_Dia_2026_Excluidos_' + safeName + '.xlsx', { compression: true })
  }

  async function publish() {
    if (publishing || !file || !checksum || !parsed.referenceDate || parsed.fatal.length ||
        !parsed.registros.length || parsed.registros.length + parsed.rejected.length !== parsed.sourceRows ||
        (parsed.rejected.length > 0 && !confirmExclusions)) return
    const before = snapshot.active?.row_count ?? 0
    if (!window.confirm(
      'Publicar nova base Fique em Dia?\n\n' +
      before + ' registros atuais → ' + parsed.registros.length + ' registros íntegros na nova base.\n' +
      parsed.rejected.length + ' registros serão EXCLUÍDOS desta publicação.\n' +
      'Data-base detectada: ' + parsed.referenceDate + '\nA versão anterior poderá ser restaurada.'
    )) return
    setPublishing(true)
    setUploadProgress(0)
    setError('')
    setMessage('')
    try {
      await substituirBaseFiqueEmDia(file.name, checksum, parsed.referenceDate, parsed.registros, setUploadProgress)
      setMessage('Base publicada: ' + parsed.registros.length + ' registros íntegros. ' +
        parsed.rejected.length + ' linhas excluídas na análise local. Guarde o relatório das exclusões.')
      setFile(null)
      setParsed(EMPTY)
      setChecksum('')
      setConfirmExclusions(false)
      await reload()
    } catch {
      setError('Publicação não concluída. A base vigente foi preservada. Confira os dados ou procure suporte técnico.')
    } finally {
      setPublishing(false)
    }
  }

  async function restore() {
    if (!snapshot.previous || publishing) return
    if (!window.confirm('Restaurar a base anterior? A base vigente será substituída.')) return
    setPublishing(true)
    setError('')
    setMessage('')
    try {
      await restaurarBaseFiqueEmDia()
      setMessage('Base anterior restaurada.')
      await reload()
    } catch {
      setError('Não foi possível restaurar a base. Tente novamente.')
    } finally {
      setPublishing(false)
    }
  }

  if (profile?.role !== 'administrador') {
    return <div className="notice error" role="alert">A atualização da base Fique em Dia é exclusiva do ADMIN.</div>
  }

  return (
    <section className="workspace-content admin-editor fique-admin">
      <div className="page-heading">
        <div>
          <button className="text-button" onClick={onBack}>← Voltar às regras</button>
          <p className="eyebrow">ACESSO EXCLUSIVO ADMIN</p>
          <h1>Fique em Dia 2026 — Gestão da Base</h1>
          <p className="muted">Valide e substitua os dados da campanha. Os dias em atraso são atualizados automaticamente na consulta.</p>
        </div>
        <span className="status-chip">Publicação controlada</span>
      </div>

      {message && <div className="notice success" role="status">{message}</div>}
      {error && <div className="notice error" role="alert">{error}</div>}
      {loading ? <div className="empty-state">Verificando base vigente...</div> : (
        <>
          <div className="fique-admin-metrics">
            <article><span>Base vigente</span><strong>{snapshot.active?.row_count ?? 0}</strong><small>registros</small></article>
            <article><span>Referência</span><strong>{snapshot.active?.reference_date ?? '—'}</strong><small>data do arquivo em produção</small></article>
            <article><span>Última publicação</span><strong>{mostrarData(snapshot.active?.imported_at)}</strong><small>histórico de versões protegido</small></article>
          </div>

          <section className="editor-form-card">
            <div className="admin-section-title">
              <div><p className="eyebrow">SUBSTITUIÇÃO DE BASE</p><h2>Importar Excel ou CSV</h2>
                <p className="muted">Apenas ADMIN. Até 20 MB. A data-base é identificada automaticamente. CPF e CNPJ são validados individualmente.</p></div>
              {snapshot.previous && <button className="secondary-button compact" disabled={publishing} onClick={() => void restore()}>
                Restaurar base anterior
              </button>}
            </div>
            <label className="fique-admin-field">
              Arquivo Excel ou CSV (.xlsx, .xls, .csv, .tsv)
              <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" onChange={(event) => void onFile(event)}
                disabled={processing || publishing}/>
            </label>
            {processing && <div className="notice info">Validando colunas, documentos, matrículas e valores...</div>}

            {file && <div className="fique-admin-preview">
              <div><span>Arquivo selecionado</span><strong>{file.name}</strong></div>
              <div><span>Linhas recebidas</span><strong>{parsed.sourceRows}</strong></div>
              <div><span>Registros publicáveis</span><strong>{parsed.registros.length.toLocaleString('pt-BR')}</strong></div>
              <div><span>Linhas excluídas</span><strong>{parsed.rejected.length.toLocaleString('pt-BR')}</strong></div>
            </div>}
            {file && <div className="notice info" role="status">
              <strong>Documentos analisados:</strong>{' '}
              {parsed.documentStats.cpfValidos.toLocaleString('pt-BR')} CPFs válidos ·{' '}
              {parsed.documentStats.cnpjValidos.toLocaleString('pt-BR')} CNPJs válidos ·{' '}
              {parsed.documentStats.cpfInvalidos.toLocaleString('pt-BR')} CPFs inválidos ·{' '}
              {parsed.documentStats.cnpjInvalidos.toLocaleString('pt-BR')} CNPJs inválidos ·{' '}
              {parsed.documentStats.irrecuperaveis.toLocaleString('pt-BR')} documentos ilegíveis/imprecisos.
            </div>}
            {file && parsed.referenceDate && <div className="notice info">
              Data-base identificada no arquivo: <strong>{parsed.referenceDate.split('-').reverse().join('/')}</strong>.
              Os dias em atraso serão atualizados diariamente na consulta, sem necessidade de nova planilha.
              Os saldos e descontos permanecem conforme esta data-base.
            </div>}
            {parsed.fatal.length > 0 && (
              <div className="notice error" role="alert">
                <strong>Não é possível publicar esta base.</strong>
                <ul>{parsed.fatal.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
              </div>
            )}
            {file && parsed.rejected.length > 0 && (
              <div className="notice warning" role="status">
                <strong>{parsed.rejected.length.toLocaleString('pt-BR')} linhas serão excluídas da nova base.</strong>
                <p>Os demais registros poderão ser publicados. Documentos com nomes ou matrículas conflitantes
                  são excluídos por completo para evitar consultas associadas à pessoa errada.</p>
                <ul>
                  {parsed.rejected.slice(0, 8).map((item) => (
                    <li key={item.line}>Linha {item.line}: {item.reason}</li>
                  ))}
                </ul>
                {parsed.rejected.length > 8 && <p>Exibindo oito exemplos. O relatório contém todas as linhas e os motivos.</p>}
                <button type="button" className="secondary-button compact"
                  onClick={downloadExcluded} disabled={publishing}>
                  ↓ Baixar relatório dos {parsed.rejected.length.toLocaleString('pt-BR')} excluídos (.xlsx)
                </button>
                <p>O arquivo Excel contém os dados originais das linhas excluídas, a linha de origem e
                  o motivo da exclusão. Ele é gerado no navegador do ADMIN; não fica disponível aos operadores.</p>
              </div>
            )}
            {file && !!checksum && !parsed.fatal.length && (
              <div className="fique-admin-publish">
                <div className="notice success">
                  Prontos para publicar: {parsed.registros.length.toLocaleString('pt-BR')} registros válidos.
                  A base vigente só será substituída após enviar e validar todos eles.
                </div>
                {parsed.rejected.length > 0 && (
                  <label className="fique-confirm-exclusions">
                    <input type="checkbox" checked={confirmExclusions} disabled={publishing}
                      onChange={(event) => setConfirmExclusions(event.target.checked)}/>
                    Estou ciente de que {parsed.rejected.length.toLocaleString('pt-BR')} linhas ficarão fora
                    da consulta e assumo a publicação somente dos registros válidos.
                  </label>
                )}
                <button type="button" className="primary-button compact"
                  onClick={() => void publish()}
                  disabled={publishing || (parsed.rejected.length > 0 && !confirmExclusions)}>
                  {publishing ? 'Enviando dados... ' + uploadProgress + '%' :
                    'Publicar ' + parsed.registros.length.toLocaleString('pt-BR') + ' registros válidos'}
                </button>
              </div>
            )}
          </section>
          {publishing && <div className="notice info" role="status">
            Importação em lotes: {uploadProgress}% concluído. Não feche esta tela até terminar.
          </div>}
          <p className="fique-muted">O arquivo completo e a relação de CPFs/CNPJs nunca são disponibilizados na área operacional.</p>
        </>
      )}
    </section>
  )
}
